import { SystemMessageBlock, SystemMessageBlockKind, SystemMessageType } from '@/models/in/SystemMessageDtoIn';

export const SYSTEM_MESSAGES_MANAGEMENT_URL = 'management/system-messages';
export const LIVE_SYSTEM_MESSAGES_URL = 'system-messages/live';

export const SYSTEM_MESSAGE_TYPES: SystemMessageType[] = ['info', 'success', 'warning', 'error'];


/** Kept in step with WebApi's SystemMessageContent, which enforces the same ceilings server-side. */
export const MAX_BLOCKS = 30;
export const MAX_BULLET_ITEMS = 20;
export const MAX_BUTTONS = 4;

/** A freshly added block of the given kind, with one empty row where a kind needs rows. */
export const emptyBlock = (kind: SystemMessageBlockKind): SystemMessageBlock => {
  switch (kind) {
    case 'bullets':
      return { kind, items: [''] };
    case 'buttons':
      return { kind, buttons: [{ label: '', url: '', variant: 'primary' }] };
    default:
      return { kind, text: '' };
  }
};

/**
 * Whether a button's address is one the site will open, mirroring SystemMessageContent.IsSafeUrl on
 * the server. Checked here as well so an admin is told while they are still editing, rather than by
 * a rejected save.
 */
export const isSafeUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) return false;

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return /^https?:\/\//i.test(trimmed);
  }

  return trimmed.startsWith('/') && !trimmed.startsWith('//');
};

/**
 * The blocks as they should be saved: trimmed, with the rows an admin left blank dropped. The
 * server does the same on the way in, so this is about the preview matching what gets stored.
 */
export const cleanBlocks = (blocks: SystemMessageBlock[]): SystemMessageBlock[] =>
  blocks
    .map((block): SystemMessageBlock | null => {
      switch (block.kind) {
        case 'bullets': {
          const items = (block.items ?? []).map((item) => item.trim()).filter(Boolean);
          return items.length > 0 ? { kind: 'bullets', items } : null;
        }
        case 'buttons': {
          const buttons = (block.buttons ?? [])
            .map((button) => ({ ...button, label: button.label.trim(), url: button.url.trim() }))
            .filter((button) => button.label && button.url && isSafeUrl(button.url));
          return buttons.length > 0 ? { kind: 'buttons', buttons } : null;
        }
        case 'image': {
          // A screenshot that came with the feature (HAT-1489): kept as it is, with its description.
          const src = (block.src ?? '').trim();
          return src ? { kind: 'image', src, text: (block.text ?? '').trim() || null } : null;
        }
        default: {
          const text = (block.text ?? '').trim();
          return text ? { kind: block.kind, text } : null;
        }
      }
    })
    .filter((block): block is SystemMessageBlock => block !== null);

/**
 * The first button whose address the site would refuse to open, so the editor can name it instead
 * of silently dropping it on save.
 */
export const firstUnsafeButtonUrl = (blocks: SystemMessageBlock[]): string | null => {
  for (const block of blocks) {
    if (block.kind !== 'buttons') continue;

    for (const button of block.buttons ?? []) {
      const url = button.url.trim();
      if (url && !isSafeUrl(url)) return url;
    }
  }

  return null;
};

/** The date input wants yyyy-MM-dd; the API hands back a full ISO timestamp at midnight. */
export const toDateInputValue = (isoDate: string | null): string => {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Whether the message is being shown to users at this moment: both its switch and its validity
 * window have to agree. The table shows this rather than the raw IsActive flag, because a message
 * that is "active" but expired is the case an admin most needs to be able to see at a glance.
 */
export const isLiveNow = (message: { isActive: boolean; validFrom: string | null; validTo: string | null }): boolean => {
  if (!message.isActive) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (message.validFrom) {
    const from = new Date(message.validFrom);
    from.setHours(0, 0, 0, 0);
    if (from > today) return false;
  }

  if (message.validTo) {
    const to = new Date(message.validTo);
    to.setHours(0, 0, 0, 0);
    if (to < today) return false;
  }

  return true;
};

/** dd/MM/yyyy, matching the DataTable's Date cell. Empty string for an unbounded end of the window. */
export const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  return Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};
